import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // Auth modal state — drives both the guard popup and the login/register form
  const [authModal, setAuthModal] = useState({
    open: false,
    tab: 0,          // 0 = login, 1 = register
    guardMessage: '', // non-empty = came from a guest-guard trigger
  });

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.me()
        .then(res => setUser(res.data.data))
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  /**
   * requireAuth — call this before any guest-restricted action.
   * If the user is authenticated, returns true (action may proceed).
   * If guest, opens the guard popup with an optional contextual message
   * and returns false (caller should abort the action).
   *
   * Usage:
   *   const handleCreateAlert = () => {
   *     if (!requireAuth('Login required to create alerts 🚀')) return;
   *     // … proceed with action
   *   };
   */
  const requireAuth = useCallback((message = 'Login required to use this feature 🚀') => {
    if (user) return true;
    setAuthModal({ open: true, tab: 0, guardMessage: message });
    return false;
  }, [user]);

  /** Open the auth modal directly on a specific tab (0=login, 1=register). */
  const openAuthModal = useCallback((tab = 0) => {
    setAuthModal({ open: true, tab, guardMessage: '' });
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModal(m => ({ ...m, open: false, guardMessage: '' }));
  }, []);

  const isAdmin = user?.role === 'ADMIN';
  const isGuest = !user;

  return (
    <AuthContext.Provider value={{
      user, setUser, loading, logout,
      isAdmin, isGuest,
      authModal, requireAuth, openAuthModal, closeAuthModal,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
